import React, { useRef, useImperativeHandle, forwardRef, useCallback } from 'react';
import { useIntl } from 'umi';
import { useControllableValue, useHover } from 'ahooks';
import ChartGameSelect from './components/ChartGameSelect';
import NewRegionSelect from '@home/components/NewRegionSelect';
import type { OptionsItemType, RegionSelectValueType } from '@home/components/NewRegionSelect/type';
import NewChartIcon from '@/pages/aigc/assets/svg/agent-new-chart.svg';
import NewChartActiveIcon from '@/pages/aigc/assets/svg/agent-new-chart-active.svg';
import useChartPopoverData from './hooks/useChartPopoverData';
import ChartPopoverTitleTip from './components/ChartPopoverTitleTip';
import InputTriggerHoverTip from '../components/InputTriggerHoverTip';
import type { ISelectedChartTagData } from './components/SelectedChartTag';
import { resolveChartSecondLevelTitle } from './utils/selectionState';
import useChartPopoverSelection from './hooks/useChartPopoverSelection';
import useChartPopoverPanel from './hooks/useChartPopoverPanel';
import useChartPopoverInteraction from './hooks/useChartPopoverInteraction';
import {
  CHART_POPOVER_RIGHT_LIST_WIDTH,
  CHART_POPOVER_RIGHT_LIST_HEIGHT,
} from './hooks/chartPopoverConstants';
import './index.scss';

interface IChartPopoverProps {
  /** 关闭浮窗时回传已选图表/报表数据 */
  onConfirm?: (selectedCharts: ISelectedChartTagData[], gameCode: string) => void;
  /** 外部已选中的图表列表，用于反推勾选态 */
  selectedCharts?: ISelectedChartTagData[];
  /** 受控浮窗开关 */
  open?: boolean;
  /** 受控浮窗开关变更回调 */
  onOpenChange?: (open: boolean) => void;
  /** 来源页面，用于决定浮层向上/向下展开 */
  from?: string;
  /** 是否处于编辑模式 */
  isEdit?: boolean;
}

export interface IChartPopoverRef {
  /** 打开浮窗，可指定游戏与默认高亮的 dashboard */
  open: (gameCode?: string, dashboardValue?: string) => void;
}

/** 新建图表浮窗：基于 NewRegionSelect 的 Tea Dropdown，按外部 selectedCharts 反推勾选态 */
const ChartPopover = forwardRef<IChartPopoverRef, IChartPopoverProps>(
  (props, ref) => {
    const { onConfirm, selectedCharts = [], from, isEdit } = props;
    const isDropDown = from === 'agentHome' || !!isEdit;
    const { formatMessage, locale } = useIntl();
    /** Copilot 仅 click 打开；其余场景 hover 打开 */
    const enableHoverOpen = from !== 'copilot';
    const triggerRef = useRef<HTMLElement>(null);
    const hoveredRef = useRef(false);
    const hovered = useHover(triggerRef);
    const [opened, setOpened] = useControllableValue<boolean>(
      props,
      {
        defaultValue: false,
        valuePropName: 'open',
        trigger: 'onOpenChange',
      },
    );

    const {
      gameList, recentList, mainGames, selectedGameOption, defaultGameCode,
      dashboardOptions, dashboardOptionsGameCode,
      selectedGameCode, handleSelectGame, loading,
      autoSelectRequest, consumeAutoSelectRequest,
    } = useChartPopoverData();

    const {
      localSelectValue,
      setLocalSelectValue,
      defaultSelectedRegion,
      regionMountKey,
      applySelection,
      confirmCurrentGame,
      resetLocalSelectValue,
      confirmCurrentGameRef,
      localSelectValueRef,
      selectedGameCodeRef,
    } = useChartPopoverSelection({
      selectedCharts,
      dashboardOptions,
      mainGames,
      locale,
      onConfirm,
      selectedGameCode,
    });

    const {
      openPanel,
      closePanel,
      handleSwitchGame,
      handleOpenChange,
    } = useChartPopoverPanel({
      opened,
      setOpened,
      selectedGameCode,
      defaultGameCode,
      loading,
      dashboardOptionsGameCode,
      handleSelectGame,
      autoSelectRequest,
      consumeAutoSelectRequest,
      applySelection,
      confirmCurrentGame,
      localSelectValue,
      resetLocalSelectValue,
      hoveredRef,
    });

    const {
      setIsGameSelectOpen,
      handleTriggerMouseEnter,
    } = useChartPopoverInteraction({
      opened,
      closePanel,
      hovered,
      hoveredRef,
      enableHoverOpen,
      openPanel,
      selectedGameCode,
      confirmCurrentGameRef,
      localSelectValueRef,
      selectedGameCodeRef,
    });

    const resolveChartSecondLevelTitleProp = useCallback(
      (item: OptionsItemType | undefined) =>
        resolveChartSecondLevelTitle(
          item,
          formatMessage({ id: 'dba_new_chart_chart_title' }),
          locale,
        ),
      [formatMessage, locale],
    );

    useImperativeHandle(ref, () => ({
      open: (gameCode?: string, dashboardValue?: string) => {
        if (
          opened &&
          gameCode &&
          gameCode === selectedGameCode &&
          dashboardValue &&
          dashboardValue === defaultSelectedRegion
        ) {
          return;
        }
        openPanel(gameCode, dashboardValue, false);
      },
    }), [openPanel, opened, selectedGameCode, defaultSelectedRegion]);

    const chartIconSrc = hovered || opened ? NewChartActiveIcon : NewChartIcon;

    const handleTriggerClick = (e: React.MouseEvent): void => {
      if (enableHoverOpen) return;
      e.stopPropagation();
      if (!opened) {
        openPanel(selectedGameCode, '', false);
      }
    };

    const chartButton = (
      <span
        ref={triggerRef}
        className="dba-new-chart-btn"
        onMouseEnter={enableHoverOpen ? handleTriggerMouseEnter : undefined}
        onClick={handleTriggerClick}
      >
        <img src={chartIconSrc} alt="new-chart" />
      </span>
    );

    const popoverHeader = (
      <div className="dba-new-chart-popover__header">
        <div className="dba-new-chart-popover__title">
          <span className="dba-new-chart-popover__title-text">
            {formatMessage({ id: 'dba_new_chart_select_title' })}
          </span>
          <ChartPopoverTitleTip />
        </div>
        <div className="dba-new-chart-popover__game-header">
          <ChartGameSelect
            gameList={gameList}
            recentList={recentList}
            selectedGameOption={selectedGameOption}
            value={selectedGameCode}
            onChange={handleSwitchGame}
            onOpen={() => setIsGameSelectOpen(true)}
            onClose={() => setIsGameSelectOpen(false)}
            name={formatMessage({ id: 'dba_new_chart_game_label' })}
            placeholder={formatMessage({ id: 'dba_new_chart_game_placeholder' })}
            overlayClassName="dba-new-chart-game-panel-overlay"
          />
        </div>
      </div>
    );

    return (
      <InputTriggerHoverTip
        enabled={!enableHoverOpen}
        content={formatMessage({ id: 'dba_copilot_input_chart_hover_tip' })}
        suppressVisible={opened}
      >
        <NewRegionSelect
          key={regionMountKey}
          options={dashboardOptions}
          radioOptions={[]}
          value={localSelectValue}
          defaultSelectedRegion={defaultSelectedRegion || undefined}
          onChange={setLocalSelectValue}
          onCloseReginSelect={(result: RegionSelectValueType) => {
            if (hoveredRef.current) return;
            setLocalSelectValue(result);
            closePanel();
          }}
          shortcutOptions={[]}
          realRegionsList={[]}
          locale={locale}
          loading={loading}
          searchTagMode="region"
          firstLevelTitle={formatMessage({ id: 'dba_new_chart_second_level_title' })}
          resolveSecondLevelTitle={resolveChartSecondLevelTitleProp}
          enableExactOverflowBubble
          enableSearchTagOverflowBubble
          leftBubblePlacement="top"
          leftBubblePlacementOffset={8}
          rightBubblePlacement="top"
          rightBubblePlacementOffset={8}
          overflowBubbleProps={{
            listRow: {
              overlayClassName: 'dba-nav-menu-overlay dba-new-chart-list-bubble',
              escapeWithReference: true,
              arrowPointAtCenter: true,
            },
            searchTag: {
              overlayClassName: 'dba-nav-menu-overlay dba-new-chart-search-tag-bubble',
              escapeWithReference: true,
              closeOnScroll: false,
              dark: true,
              arrowPointAtCenter: true,
              placement: 'top',
              placementOffset: 10,
            },
          }}
          rightListWidth={CHART_POPOVER_RIGHT_LIST_WIDTH}
          rightListHeight={CHART_POPOVER_RIGHT_LIST_HEIGHT}
          button={chartButton}
          header={popoverHeader}
          overlayClassName="dba-new-chart-popover-overlay dbc-region-select-box dba-nav-menu-overlay"
          boxClassName="dba-new-chart-popover-box"
          className="dba-new-chart-popover-trigger"
          appearance="pure"
          open={opened}
          onOpenChange={handleOpenChange}
          placement={isDropDown ? 'bottom-start' : 'top-start'}
          placementOffset={4}
          flipped={false}
          escapeWithReference={isDropDown}
          updateOnChildrenChange
          closeOnScroll={false}
        />
      </InputTriggerHoverTip>
    );
  });

ChartPopover.displayName = 'ChartPopover';

export default ChartPopover;
